import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

export default function NewsletterSignup({ variant = "default", source = "website" }) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [consent, setConsent] = useState(true);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email) {
            toast.error('Veuillez entrer votre email');
            return;
        }

        setLoading(true);
        try {
            await apiRequest('/marketing/newsletter/subscribe', {
                method: 'POST',
                body: JSON.stringify({
                    email,
                    name: name || null,
                    source,
                    consent_marketing: consent,
                    consent_partners: false
                })
            });
            setSuccess(true);
            toast.success('Bienvenue ! Vérifiez votre email pour votre code promo');
        } catch (error) {
            toast.error(error.message || 'Erreur lors de l\'inscription');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className={`flex items-center gap-3 ${variant === 'compact' ? 'text-sm' : ''}`}>
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-green-600 dark:text-green-400">
                    Merci ! Vérifiez votre boîte mail pour votre code -10%
                </span>
            </div>
        );
    }

    if (variant === 'compact') {
        return (
            <form onSubmit={handleSubmit} className="flex gap-2" data-testid="newsletter-compact">
                <Input
                    type="email"
                    placeholder="Votre email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-background/50"
                    data-testid="newsletter-email"
                />
                <Button type="submit" disabled={loading} className="shrink-0">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'OK'}
                </Button>
            </form>
        );
    }

    if (variant === 'popup') {
        return (
            <div className="text-center space-y-4" data-testid="newsletter-popup">
                <div className="flex justify-center">
                    <div className="bg-primary/10 p-4 rounded-full">
                        <Gift className="h-10 w-10 text-primary" />
                    </div>
                </div>
                <h3 className="text-xl font-bold">-10% sur votre première commande</h3>
                <p className="text-muted-foreground">
                    Inscrivez-vous à notre newsletter et recevez un code promo exclusif !
                </p>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <Input
                        type="text"
                        placeholder="Votre prénom (optionnel)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <Input
                        type="email"
                        placeholder="Votre email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="newsletter-email"
                    />
                    <div className="flex items-start gap-2 text-left">
                        <Checkbox
                            id="consent-popup"
                            checked={consent}
                            onCheckedChange={setConsent}
                        />
                        <Label htmlFor="consent-popup" className="text-xs text-muted-foreground">
                            J'accepte de recevoir les offres et conseils d'O.C.A Piscine
                        </Label>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full">
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Inscription...
                            </>
                        ) : (
                            <>
                                <Mail className="h-4 w-4 mr-2" />
                                Recevoir mon code -10%
                            </>
                        )}
                    </Button>
                </form>
            </div>
        );
    }

    // Default variant
    return (
        <div className="space-y-4" data-testid="newsletter-default">
            <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <h4 className="font-semibold">Newsletter</h4>
            </div>
            <p className="text-sm text-muted-foreground">
                Recevez nos conseils d'entretien et offres exclusives
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                    type="email"
                    placeholder="Votre email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="newsletter-email"
                />
                <div className="flex items-start gap-2">
                    <Checkbox
                        id="consent-default"
                        checked={consent}
                        onCheckedChange={setConsent}
                    />
                    <Label htmlFor="consent-default" className="text-xs text-muted-foreground">
                        J'accepte de recevoir les communications marketing
                    </Label>
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                    {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        "S'inscrire"
                    )}
                </Button>
            </form>
            <p className="text-xs text-muted-foreground">
                🎁 -10% offert sur votre 1ère commande
            </p>
        </div>
    );
}

// Popup newsletter that appears after delay
export function NewsletterPopup({ delay = 5000 }) {
    const [show, setShow] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    React.useEffect(() => {
        // Don't show on certain pages
        const excludedPaths = ['/chat', '/admin', '/checkout', '/cart', '/calculateur'];
        if (excludedPaths.some(path => window.location.pathname.startsWith(path))) {
            return;
        }
        
        // Check if already shown
        const shown = localStorage.getItem('newsletter_popup_shown');
        if (shown) return;

        const timer = setTimeout(() => {
            setShow(true);
        }, delay);

        return () => clearTimeout(timer);
    }, [delay]);

    const handleDismiss = () => {
        setDismissed(true);
        setShow(false);
        localStorage.setItem('newsletter_popup_shown', 'true');
    };

    if (!show || dismissed) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-background rounded-2xl p-6 max-w-md w-full shadow-2xl relative animate-in fade-in zoom-in duration-300">
                <button
                    onClick={handleDismiss}
                    className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                >
                    ✕
                </button>
                <NewsletterSignup variant="popup" source="popup" />
                <button
                    onClick={handleDismiss}
                    className="mt-4 text-sm text-muted-foreground hover:underline w-full text-center"
                >
                    Non merci, peut-être plus tard
                </button>
            </div>
        </div>
    );
}
